import { TimeZoneOptions } from './timezone';

export const IMPORT_SOURCE = {
  SHEET: 'sheet',
  FILE: 'file',
};

export const UPLOAD_STEPS = {
  UPLOAD: 'upload',
  REVIEW: 'review',
};

export const ACCEPTED_FILE_TYPES = ['.csv', '.xlsx', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

export const IMPORT_DATE_FORMAT = 'MM/DD/YYYY hh:mm A';
export const IMPORT_DATE_FORMAT_24H = 'MM/DD/YYYY HH:mm';
const TIMEZONES_SUGGESTION = TimeZoneOptions.map(({ value }) => value).join(', ');

export const IMPORT_CHANNEL = {
  MESSENGER: 'messenger',
  EMAIL: 'email',
  SMS: 'sms',
};

export const IMPORT_MEDIA_TYPES = Object.values(IMPORT_CHANNEL).map((channel) => ({ label: channel, value: channel }));

export const IMPORT_SERVER_STATUS = {
  READY: 'READY',
  ERROR: 'ERROR',
  WARNING: 'WARNING',
};

export const IMPORT_ROW_STATUS = {
  READY: 'ready',
  FIX: 'fix',
  WARNING: 'warning',
  DONE: 'done',
  IGNORED: 'ignored',
};

export const IMPORT_ERRORS = {
  NAME_REQUIRED: 'Name is required.',
  INVALID_CHANNEL: 'Choose Email, Messenger or SMS.',
  SUBJECT_REQUIRED: 'Subject is required for Email.',
  TITLE_REQUIRED: 'Title is required for Messenger.',
  CONTENT_REQUIRED: 'Content is required.',
  BLOCKED_HTML: 'Content contains HTML that is not allowed: {tags}. Remove it and try again.',
  TIMEZONE_REQUIRED: 'Timezone is required.',
  INVALID_DATE_FORMAT: 'Date & Time must be in the format MM/DD/YYYY HH:MM AM/PM or MM/DD/YYYY HH:MM (24-hour).',
  INVALID_DATE: 'Not a real date.',
  PAST_DATE: 'Date & Time must be in the future.',
  INVALID_TIMEZONE: `Use an IANA timezone, e.g. ${TIMEZONES_SUGGESTION}.`,
  INVALID_PROMOTIONAL: 'Use TRUE or FALSE.',
  INVALID_DRIVER_IDS: 'Use driver IDs separated by commas.',
  REGION_REQUIRED: 'Driver Region is required for a Driver Pool or Driver Crew.',
  INVALID_REGION: 'Invalid region code(s):',
  POOL_NOT_FOUND: 'Driver pool not found:',
  CREW_NOT_FOUND: 'Driver crew not found:',
  CREW_OUTSIDE_REGIONS: 'Driver crew(s) not in the selected regions:',
  DRIVER_IDS_REQUIRED: 'Driver is required when Date & time is set.',
  POOL_NO_DRIVERS: 'No drivers found for the selected pools.',
  CREW_NO_DRIVERS: 'No drivers found for the selected crews.',
  // When the API marks a row ERROR without saying which field; cleared by any edit to the row.
  SERVER_REJECTED: 'This row was rejected by the server. Review it before importing.',
};

export const IMPORT_CSV_TEMPLATE_FILE_NAME = 'announcement-template.csv';

// To download CSV template
export const IMPORT_CSV_TEMPLATE = [
  { header: 'Channel', sample: '<email/messenger/sms>' },
  { header: 'Promotional?', sample: '<TRUE/FALSE or YES/NO>' },
  { header: 'Name', sample: '' },
  { header: 'Description', sample: '' },
  { header: 'Subject Line/Title', sample: '' },
  { header: 'Content', sample: '<HTML for email, messenger; plain text for SMS>' },
  { header: 'Date & Time', sample: '<MM/DD/YYYY HH:MM AM/PM>' },
  { header: 'Timezone', sample: '<America/New_York>' },
  { header: 'Add by driver IDs', sample: '<001, 002>' },
  { header: 'Driver Region', sample: '<LAS, LAX>' },
  { header: 'Driver Pool', sample: '' },
  { header: 'Driver Crew', sample: '' },
];

export const IMPORT_TEMPLATE_COLUMNS = IMPORT_CSV_TEMPLATE.map(({ header }) => header);

// Define columns for Review table, these are editable fields
export const IMPORT_REVIEW_COLUMNS = [
  { key: 'status', label: 'Status', width: '147px' },
  { key: 'media_type', label: 'Channel', width: '140px', validate: true },
  { key: 'is_promotional', label: 'Promotional?', width: '100px', validate: true },
  { key: 'name', label: 'Name', width: '200px', validate: true },
  { key: 'description', label: 'Description', width: '200px' },
  { key: 'subject', label: 'Subject Line / Title', width: '200px', validate: true },
  { key: 'communication', label: 'Message / Content', width: '260px', validate: true },
  { key: 'origin_schedule_time', label: 'Date & Time', width: '200px', validate: true },
  { key: 'schedule_timezone', label: 'Timezone', width: '180px', validate: true },
  { key: 'drivers', label: 'Driver IDs', width: '170px', validate: true },
  { key: 'driver_regions', label: 'Driver Region', width: '200px', validate: true },
  { key: 'driver_pool_ids', label: 'Driver Pool', width: '250px', validate: true },
  { key: 'driver_crew_ids', label: 'Driver Crew', width: '250px', validate: true },
];

// Shared by the review header and every row so the virtualised rows stay aligned with the header.
export const IMPORT_REVIEW_GRID = IMPORT_REVIEW_COLUMNS.map((column) => column.width).join(' ');

export const IMPORT_VALIDATED_FIELDS = IMPORT_REVIEW_COLUMNS.filter(({ validate }) => !!validate).map(({ key }) => key);

// The import API may key a field's errors by another name (e.g. `driver_crews` for the crew ids);
// errors under any of these names are shown on that field's cell.
export const IMPORT_ERROR_ALIASES = {
  // The API files Date & Time errors under the schedule time it computes from it.
  origin_schedule_time: ['schedule_time'],
  driver_regions: ['driver_region'],
  driver_pool_ids: ['driver_pools', 'origin_driver_pools'],
  driver_crew_ids: ['driver_crews', 'origin_driver_crews'],
};

// The validate API's "no drivers" error on Pool and Crew, e.g. "No drivers found in the specified driver
// crews." It's recognised by its wording because the API gives no error code, and it has to be told apart
// from the other errors on those fields (e.g. "Driver crew not found: YEN Crew"), which still count. The
// API checks pools and crews for drivers even when the row has driver IDs, where only the IDs are used,
// so for those rows this one error is redundant and dropped (see dropRedundantErrorsWithDriverIds). Matching
// the start, and pools as well as crews, keeps it working if the API words the pool message the same way.
export const SERVER_NO_DRIVERS_ERROR = /^No drivers found in the specified driver (pools|crews)/i;

// The validate API's "regions required" error on Region, e.g. "Valid driver regions are required when driver
// pools are set.", likewise recognised by its wording (other region errors still count). A row with driver
// IDs needs no region, so it's dropped for those rows too.
export const SERVER_REGIONS_REQUIRED_ERROR = /^Valid driver regions are required when driver (pools|crews) are set/i;

export const SELECT_REGION_FIRST = 'Select a Driver Region first.';
// Tags Content can't contain, on any channel.
export const IMPORT_BLOCKED_HTML_TAGS = ['script', 'iframe', 'video'];
// Review table: the error red of cells, chips and tooltips.
export const IMPORT_ERROR_COLOR = '#c62828';
export const IMPORT_OPTIONS_PAGE_SIZE = 1000;
export const IMPORT_VALIDATE_URL = '/driver-announcements/bulk/validate';
export const IMPORT_BULK_URL = '/driver-announcements/bulk';
// Bulk create error key of a row created but not scheduled (a warning); any other errors mean not created.
export const IMPORT_SCHEDULE_ERROR_KEY = 'schedule';
export const GOOGLE_SHEET_LINK_PATTERN = /^https:\/\/docs\.google\.com\/spreadsheets\/d\/[\w-]+/;
export const IMPORT_FILE_PATTERN = /\.(csv|xlsx|xls)$/i;

export const IMPORT_UPLOAD_ERRORS = {
  INVALID_SHEET_LINK: 'Enter a Google Sheets link, like https://docs.google.com/spreadsheets/d/...',
  UNSUPPORTED_FILE: 'Unsupported file type. Upload a .csv .xlsx or .xls file.',
};
